import { useContext, useState, ReactNode } from "react";
import {
	Button,
	Typography,
	TextField,
	CircularProgress,
	Tabs,
	Tab,
	Box,
	FormHelperText,
	Card,
	CardContent,
	Stack,
	Container,
	CardActions,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { activateUser, deactivateUser, register } from "../../services/userManagement";
import TabPanel from "../TabPanel";
import { colours } from "../../shared/colors";
import { GENERIC_ERROR, passwordValidation } from "../../helper/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
// import "../../shared/scss/components/adminPage.scss"; // Importing SCSS file

const Loading = () => (
	<Box
		display="flex"
		justifyContent="center"
		alignItems="center"
		minHeight="2em"
		px="0.5em"
		width="100%"
		position="relative"
	>
		<CircularProgress
			size={"2em"}
			sx={{
				color: "white",
				position: "absolute",
				zIndex: 1,
			}}
		/>
	</Box>
);
/***
 * PASSWORD RULES:
 * - at least 1 digit
 * - at least 1 lowercase
 * - at least 1 uppercase
 * - at least 1 non alphanumeric
 * - at least 10 unique characters
 * - at least 16 characters in length
 *
 * EMAIL RULES:
 * - Allowed characters: abcdefghijklmnopqrstuvw789ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-._@+
 ***/

/**
 * Yup validation schema for the user management forms.
 */
export const validationSchema = {
	register: Yup.object({
		email: Yup.string()
			.matches(
				/^[a-zA-Z0-9-._@+]+$/,
				"Email can only have letters, digits or -._@+ characters"
			)
			.email("Please enter a valid email address")
			.required("Email address is required"),
		password: passwordValidation,
		confirmPassword: Yup.string()
			.oneOf([Yup.ref("password"), null], "Password must match")
			.required("Please confirm your password"),
	}),
	activate: Yup.object({
		email: Yup.string()
			.email("Please enter a valid email address")
			.required("Email address is required"),
		activate: Yup.boolean(),
	}),
};

/**
 * Component for the login page.
 *
 * This component renders the login page user interface, allowing users to input their
 * email and password for authentication. It provides form validation, error handling,
 * and login functionality. Upon successful login, it navigates to the home page.
 *
 * @returns {JSX.Element} The rendered login page UI.
 */
const AdminPage = () => {
    const { admin } = useSelector((state: RootState) => state.authSlice);

	//   const [ loading, setLoading] = useState(false);
	const [genericError, setGenericError] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [tabValue, setTabValue] = useState(0);

	const handleTabChange = (
		event: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent<Element, Event>,
		newValue: number
	) => {
		setTabValue(newValue);
		// formik.resetForm();
	};

	// Function to set accessibility properties for tabs
	function a11yProps(index: number) {
		return {
			id: `admin-tab-${index}`,
			"aria-controls": `admin-tabpanel-${index}`,
		};
	}

	const buildRegistration = () => {
		return (
			<Formik
				initialValues={{
					email: "",
					password: "",
					confirmPassword: "",
					successMessage: "",
					errorMessage: "",
				}}
				validationSchema={validationSchema.register}
				enableReinitialize
				onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
					console.log("Formik values", values);
					await register(values.email, values.password)
						.then((res) => {
							console.log("Registration response", res);
							if (res?.status === "Success") {
								resetForm();
								setFieldValue("successMessage", res.message);
								setFieldValue("errorMessage", "");
							} else {
								console.log("Error registering user", res);
								setFieldValue("errorMessage", res.response.data.message);
								setFieldValue("successMessage", "");
							}
							setSubmitting(false);
						})
						.catch((error) => console.log("Error", error));
				}}
			>
				{(formik) => (
					<Form>
						<Box py="1em">
							<Typography
								variant="h3"
								sx={{
									fontSize: "24px",
									fontWeight: 600,
									color: colours.darkGrey.main,
								}}
							>
								Register
							</Typography>
						</Box>

						<Typography variant="subtitle1" sx={{ mb: "2em" }}>
							Broker needs to be set up in DIMS first. Make sure to enter the exact
							email address as used in DIMS
						</Typography>

						<Box width={{ sm: "100%", md: "50%" }} pb="1em">
							<Stack spacing={3}>
								<TextField
									label="Email address"
									// variant="outlined"
									name="email"
									disabled={formik.isSubmitting}
									value={formik.values.email}
									onChange={formik.handleChange}
									error={formik.touched.email && Boolean(formik.errors.email)}
									helperText={formik.touched.email && formik.errors.email}
								/>
								<TextField
									label="Password"
									// variant="outlined"
									type="password"
									name="password"
									disabled={formik.isSubmitting}
									value={formik.values.password}
									onChange={formik.handleChange}
									error={
										(formik.touched.password &&
											Boolean(formik.errors.password)) ||
										genericError
									}
									helperText={
										genericError
											? errorMessage
											: formik.touched.password && formik.errors.password
									}
								/>
								<TextField
									label="Confirm password"
									// variant="outlined"
									type="password"
									name="confirmPassword"
									disabled={formik.isSubmitting}
									value={formik.values.confirmPassword}
									onChange={formik.handleChange}
									error={
										(formik.touched.confirmPassword &&
											Boolean(formik.errors.confirmPassword)) ||
										genericError
									}
									helperText={
										genericError
											? errorMessage
											: formik.touched.confirmPassword &&
											  formik.errors.confirmPassword
									}
								/>
							</Stack>
						</Box>
						{formik.values.errorMessage.length > 0 && (
							<FormHelperText error>{formik.values.errorMessage}</FormHelperText>
						)}
						{formik.values.successMessage.length > 0 && (
							<FormHelperText sx={{ color: "green" }}>
								{formik.values.successMessage}
							</FormHelperText>
						)}
						<CardActions sx={{ display: "flex", justifyContent: "end" }}>
							<Button type="submit" variant="contained">
								{formik.isSubmitting ? <Loading /> : "Register"}
							</Button>
						</CardActions>
						<FormHelperText>v. 1.5.789</FormHelperText>
					</Form>
				)}
			</Formik>
		);
	};

	const buildUserManagement = () => {
		return (
			<Formik
				initialValues={{ email: "", activate: true, successMessage: "", errorMessage: "" }}
				validationSchema={validationSchema.activate}
				enableReinitialize
				validateOnBlur
				onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
					console.log("Activate:", values.activate);
					console.log("Formik values", values);
					const result = values.activate
						? await activateUser(values.email)
						: await deactivateUser(values.email);
					console.log("Result", result);
					if (result.status === "Success") {
						console.log(
							`${values.activate ? "Act" : "Deact"}ivated user`,
							result.response
						);
						resetForm();
						setFieldValue("successMessage", result.message);
						setFieldValue("errorMessage", "");
						setSubmitting(false);
					} else {
						console.log(
							`Error ${values.activate ? "act" : "deact"}ivating user ${
								values.email
							}`,
							result
						);
						setFieldValue("successMessage", "");
						let errorMessage =
							result.response.status === "Error"
								? result.response.message ??
                                GENERIC_ERROR
								: `${result.message}: ${result.response?.data ?? ""}`;
						setFieldValue("errorMessage", errorMessage);
						setSubmitting(false);
					}
				}}
			>
				{(formik) => (
					<Form>
						<Box pt="1em" pb="1.5em">
							<Typography
								variant="h3"
								sx={{
									fontSize: "24px",
									fontWeight: 600,
									color: colours.darkGrey.main,
									mb: "1em",
								}}
							>
								Manage user
							</Typography>
							<Stack spacing={2}>
								<Typography variant="subtitle1">
									Broker needs to be set up in DIMS first. Make sure to enter the
									exact email address as used in DIMS
								</Typography>
								<Typography variant="subtitle1">
									Broker needs to be registered before they can be activated.
								</Typography>
							</Stack>
						</Box>

						<Box width={{ sm: "100%", md: "50%" }} pb="1em">
							<TextField
								label="Email address"
								name="email"
								fullWidth
								disabled={formik.isSubmitting}
								value={formik.values.email}
								onChange={formik.handleChange}
								error={formik.touched.email && Boolean(formik.errors.email)}
								helperText={formik.touched.email && formik.errors.email}
							/>
						</Box>
						{formik.values.errorMessage.length > 0 && (
							<FormHelperText error>{formik.values.errorMessage}</FormHelperText>
						)}
						{formik.values.successMessage.length > 0 && (
							<FormHelperText sx={{ color: "green" }}>
								{formik.values.successMessage}
							</FormHelperText>
						)}
						<CardActions sx={{ display: "flex", justifyContent: "end" }}>
							<Button
								type="submit"
								variant="contained"
								onClick={
									formik.isSubmitting
										? undefined
										: () =>
												formik.handleChange({
													target: {
														name: "activate",
														value: true,
													},
												})
								}
							>
								{formik.isSubmitting ? <Loading /> : "Activate"}
							</Button>
							<Button
								type="submit"
								variant="outlined"
								color="warning"
								sx={{
									color: "crimson",
									border: "1px solid crimson",
									"&:hover": {
										border: "1px solid crimson",
									},
								}}
								onClick={
									formik.isSubmitting
										? undefined
										: () =>
												formik.handleChange({
													target: {
														name: "activate",
														value: false,
													},
												})
								}
							>
								{formik.isSubmitting ? <Loading /> : "Deactivate"}
							</Button>
						</CardActions>
					</Form>
				)}
			</Formik>
		);
	};

	const buildUnauthorised = () => {
		return (
			<CardContent>
				<Stack spacing={2}>
					<Typography
						variant="h3"
						sx={{
							fontSize: "24px",
							fontWeight: 600,
							color: colours.darkGrey.main,
						}}
					>
						Not authorised
					</Typography>
					<Typography variant="subtitle1">
						You are unauthorised to view this page.
					</Typography>
				</Stack>
			</CardContent>
		);
	};

	return (
		<Card sx={{ width: "80%" }}>
			{admin ? (
				<CardContent sx={{ py: "0.5em" }}>
					<Tabs value={tabValue} onChange={(e, v) => handleTabChange(e, v)}>
						<Tab label="Register User" {...a11yProps(0)} />
						<Tab label="User Management" {...a11yProps(1)} />
					</Tabs>
					<TabPanel value={tabValue} index={0}>
						{buildRegistration()}
					</TabPanel>
					<TabPanel value={tabValue} index={1}>
						{buildUserManagement()}
					</TabPanel>
				</CardContent>
			) : (
				buildUnauthorised()
			)}
		</Card>
	);
};

export default AdminPage;
